/**
 * AdminMyProvince — the working screen for a Bishop or Overseer.
 *
 * Everything beneath their unit in one list: add a district or assembly,
 * edit what is there, appoint a pastor or elder, open a church's workspace.
 * Anything outside their province simply isn't here, and the server refuses
 * it anyway.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import ProvinceOverview from '@/components/province/ProvinceOverview';
import ProvinceRoleReport from '@/components/province/ProvinceRoleReport';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Crown, Plus, Loader2, ArrowLeft, Church, Edit3, UserPlus, ExternalLink, Landmark,
} from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HX = '/api/churches-hx';
const CHILD_LEVELS = [
  ['district', 'District'],
  ['assembly', 'Assembly / local church'],
  ['ministry', 'Ministry / department'],
  ['home_group', 'Home group'],
];

const api = async (path, opts = {}) => {
  const r = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(typeof e.detail === 'string' ? e.detail : `Request failed (${r.status})`);
  }
  return r.json();
};

export default function AdminMyProvince() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(null);
  const [editing, setEditing] = useState(null);
  const [appointing, setAppointing] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['my-offices'],
    queryFn: () => api(`${HX}/my-offices`),
    retry: false,
  });
  const { data: meta } = useQuery({ queryKey: ['hx-meta'], queryFn: () => api(`${HX}/meta/levels`) });

  const offices = data?.offices || [];
  const units = data?.units || [];
  const seedIds = new Set(offices.map((o) => o.unit_id));
  const parents = useMemo(
    () => units.filter((u) => ['hq', 'country', 'province', 'region', 'district'].includes((u.level || '').toLowerCase())),
    [units],
  );
  const refresh = () => qc.invalidateQueries({ queryKey: ['my-offices'] });

  if (isLoading) {
    return <div className="py-20 text-center text-slate-400" data-testid="my-province-loading">
      <Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  }

  if (data?.is_super || offices.length === 0) {
    return <SuperAdminProvinceView isSuper={!!data?.is_super} />;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5" data-testid="admin-my-province">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to="/admin" className="text-xs text-slate-400 hover:text-slate-700 inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Back to dashboard
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mt-1">
            <Crown className="h-5 w-5 text-amber-600" /> My Province
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {offices.map((o) => `${o.label} of ${o.unit_name}`).join(' · ')} — {units.length} unit(s) under you
          </p>
        </div>
        <Button onClick={() => setAdding({ level: 'assembly', parent_church_id: offices[0]?.unit_id || '', name: '', city: '', country: '' })}
                data-testid="my-province-add">
          <Plus className="h-4 w-4 mr-1" /> Add a district or assembly
        </Button>
      </div>

      <Tabs defaultValue="overview">
        <TabsList data-testid="my-province-tabs">
          <TabsTrigger value="overview" data-testid="my-province-tab-overview">At a glance</TabsTrigger>
          <TabsTrigger value="units" data-testid="my-province-tab-units">Units ({units.length})</TabsTrigger>
          <TabsTrigger value="roles" data-testid="my-province-tab-roles">Role gaps</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <ProvinceOverview unitId={offices[0]?.unit_id} />
        </TabsContent>

        <TabsContent value="roles" className="mt-4">
          <ProvinceRoleReport unitId={offices[0]?.unit_id} canEditRequirements={false} />
        </TabsContent>

        <TabsContent value="units" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Your units ({units.length})</CardTitle>
              <CardDescription>
                Edit details, appoint pastors and elders, or open a church's own workspace.
                Closing a church down stays with a Super Admin.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
                    <th className="text-left py-2 px-3">Name</th>
                    <th className="text-left py-2 px-3">Level</th>
                    <th className="text-left py-2 px-3">Where</th>
                    <th className="text-right py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {units.map((u) => (
                    <tr key={u.id} className="border-b last:border-0 hover:bg-slate-50"
                        data-testid={`my-province-row-${u.id}`}>
                      <td className="py-2 px-3 font-medium text-slate-800">
                        <span className="inline-flex items-center gap-1.5">
                          {seedIds.has(u.id) ? <Landmark className="h-3.5 w-3.5 text-amber-600" />
                            : <Church className="h-3.5 w-3.5 text-slate-400" />}
                          {u.name}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <Badge variant="outline" className="text-[10px] capitalize">{(u.level || '').replace('_', ' ')}</Badge>
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-500">
                        {[u.city, u.country].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <div className="inline-flex gap-1.5 flex-wrap justify-end">
                          <Button variant="outline" size="sm" className="h-7 px-2 text-xs"
                                  onClick={() => setEditing(u)} data-testid={`my-province-edit-${u.id}`}>
                            <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 px-2 text-xs"
                                  onClick={() => setAppointing(u)} data-testid={`my-province-appoint-${u.id}`}>
                            <UserPlus className="h-3.5 w-3.5 mr-1" /> Appoint
                          </Button>
                          <Link to={`/admin/churches/${u.id}`}
                                className="inline-flex items-center h-7 px-2 text-xs rounded-md border text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                data-testid={`my-province-open-${u.id}`}>
                            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {adding && (
        <UnitDialog draft={adding} parents={parents} onClose={() => setAdding(null)}
                    onSaved={() => { refresh(); setAdding(null); }} />
      )}
      {editing && (
        <EditDialog unit={editing} onClose={() => setEditing(null)}
                    onSaved={() => { refresh(); setEditing(null); }} />
      )}
      {appointing && (
        <AppointDialog unit={appointing} offices={meta?.roles || []}
                       onClose={() => setAppointing(null)} onSaved={() => setAppointing(null)} />
      )}
    </div>
  );
}

// A Super Admin has no single province, so they pick the branch to look at.
function SuperAdminProvinceView({ isSuper }) {
  const [unitId, setUnitId] = useState('');
  const { data: churches = [] } = useQuery({
    queryKey: ['admin-churches-oversight'],
    queryFn: () => api('/api/admin/churches?is_active=true'),
    enabled: isSuper,
  });
  const units = churches.filter((c) => ['hq', 'country', 'province', 'region', 'district']
    .includes((c.level || '').toLowerCase()));

  if (!isSuper) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="my-province-empty">
        <Crown className="h-8 w-8 mx-auto text-slate-300 mb-3" />
        <p className="text-sm text-slate-600">You do not hold an office over a province yet.</p>
        <Link to="/admin/churches" className="text-sm text-emerald-700 hover:underline mt-2 inline-block">
          Go to Churches →
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5" data-testid="province-super-view">
      <div>
        <Link to="/admin" className="text-xs text-slate-400 hover:text-slate-700 inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to dashboard
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mt-1">
          <Crown className="h-5 w-5 text-amber-600" /> Province Insights
        </h1>
        <p className="text-sm text-slate-500 mt-1">Choose a branch to see how it is doing.</p>
      </div>
      <div className="max-w-sm">
        <Select value={unitId} onValueChange={setUnitId}>
          <SelectTrigger data-testid="province-super-unit"><SelectValue placeholder="Choose a province, region or district" /></SelectTrigger>
          <SelectContent>
            {units.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.name} <span className="text-[10px] text-slate-400 ml-1 capitalize">{u.level}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {unitId && (
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview" data-testid="province-super-tab-overview">At a glance</TabsTrigger>
            <TabsTrigger value="roles" data-testid="province-super-tab-roles">Role gaps</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <ProvinceOverview unitId={unitId} />
          </TabsContent>
          <TabsContent value="roles" className="mt-4">
            <ProvinceRoleReport unitId={unitId} canEditRequirements />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function UnitDialog({ draft, parents, onClose, onSaved }) {
  const [form, setForm] = useState(draft);
  const save = useMutation({
    mutationFn: () => api('/api/admin/churches', {
      method: 'POST',
      body: JSON.stringify({
        name: form.name, level: form.level, organisation_type: form.level,
        parent_church_id: form.parent_church_id,
        city: form.city, country: form.country,
      }),
    }),
    onSuccess: () => { toast.success(`${form.name} added`); onSaved(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="my-province-add-dialog">
        <DialogHeader>
          <DialogTitle>Add a unit</DialogTitle>
          <DialogDescription>It joins your province and you can run it straight away.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>What is it</Label>
            <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
              <SelectTrigger data-testid="my-province-level"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CHILD_LEVELS.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Sits under</Label>
            <Select value={form.parent_church_id} onValueChange={(v) => setForm({ ...form, parent_church_id: v })}>
              <SelectTrigger data-testid="my-province-parent"><SelectValue placeholder="Choose the province or district" /></SelectTrigger>
              <SelectContent>
                {parents.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} <span className="text-[10px] text-slate-400 ml-1 capitalize">{p.level}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="e.g. Faith World Ministries · Chitungwiza" data-testid="my-province-name" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="my-province-city" /></div>
            <div><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="my-province-country" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!form.name.trim() || !form.parent_church_id || save.isPending}
                  onClick={() => save.mutate()} data-testid="my-province-save">
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ unit, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: unit.name || '', city: unit.city || '', country: unit.country || '',
  });
  const save = useMutation({
    mutationFn: () => api(`/api/admin/churches/${unit.id}`, {
      method: 'PUT', body: JSON.stringify(form),
    }),
    onSuccess: () => { toast.success('Saved'); onSaved(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="my-province-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit · {unit.name}</DialogTitle>
          <DialogDescription className="capitalize">{(unit.level || '').replace('_', ' ')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="my-province-edit-name" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={save.isPending} onClick={() => save.mutate()} data-testid="my-province-edit-save">
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AppointDialog({ unit, offices, onClose, onSaved }) {
  const [form, setForm] = useState({ user_id: '', role_type: 'pastor', title: '' });
  const { data: admins = [] } = useQuery({
    queryKey: ['admins-for-office'], queryFn: () => api('/api/admin/admins'),
  });
  const save = useMutation({
    mutationFn: () => api(`${HX}/leadership`, {
      method: 'POST',
      body: JSON.stringify({ ...form, church_id: unit.id, is_active: true }),
    }),
    onSuccess: (res) => {
      toast.success(res?.awaiting_approval
        ? 'Proposed — a Super Admin approves oversight offices'
        : 'Appointed');
      onSaved();
    },
    onError: (e) => toast.error(e.message),
  });
  // An Overseer appoints the offices that serve one assembly.
  const options = offices.filter((o) => o.scope === 'unit');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="my-province-appoint-dialog">
        <DialogHeader>
          <DialogTitle>Appoint at {unit.name}</DialogTitle>
          <DialogDescription>Pastors and elders you appoint here start immediately.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Office</Label>
            <Select value={form.role_type} onValueChange={(v) => setForm({ ...form, role_type: v })}>
              <SelectTrigger data-testid="my-province-office"><SelectValue /></SelectTrigger>
              <SelectContent>
                {options.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Who</Label>
            <Select value={form.user_id}
                    onValueChange={(v) => setForm({
                      ...form, user_id: v,
                      title: admins.find((a) => a.id === v)?.name || '',
                    })}>
              <SelectTrigger data-testid="my-province-person"><SelectValue placeholder="Choose an admin login" /></SelectTrigger>
              <SelectContent>
                {admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || a.email} · {a.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!form.user_id || save.isPending} onClick={() => save.mutate()}
                  data-testid="my-province-appoint-save">
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Appoint
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
